import styled from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import { blueGreyMui, greyMui, rydeColors } from "@ryde/theme";

export const HubBookingsAccordionButton = styled.button.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "hub-bookings-accordion",
  },
)<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 24px 0 8px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  width: 100%;
  font-size: 18px;
  font-weight: 600;
  color: ${blueGreyMui._900};

  .hub-schedule__count {
    font-size: 14px;
    font-weight: 400;
    color: ${blueGreyMui._600};
  }

  .hub-schedule__expected-rides {
    font-size: 12px;
    font-weight: 400;
    color: ${blueGreyMui._600};
  }

  .hub-schedule__chevron {
    margin-left: auto;
    border: solid ${blueGreyMui._600};
    border-width: 0 2px 2px 0;
    display: inline-block;
    padding: 4px;
    transform: rotate(${props => (props.$isOpen ? "45deg" : "-135deg")});
    transition: transform 0.2s ease;
  }
`;

export const HubBookingsWarning = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "hub-bookings-warning",
})`
  margin: 8px 0;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  color: ${rydeColors.orange};
  background: ${greyMui._100};
`;

export const HubBookingsWrapper = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "hub-bookings-wrapper",
})`
  margin: 16px 0 24px;
  border: 1px solid ${blueGreyMui._100};
  border-radius: 8px;
  overflow: hidden;

  .hub-schedule__row {
    display: grid;
    grid-template-columns: 80px 80px minmax(0, 1fr) minmax(0, 1fr) 90px 60px;
    align-items: center;
    padding: 10px 12px;
    border-bottom: 1px solid ${blueGreyMui._100};
    font-size: 12px;
    font-weight: 500;
    color: ${rydeColors.black};

    &:last-child {
      border-bottom: none;
    }
  }

  .hub-schedule__row--header {
    padding: 8px 12px;
    background-color: ${blueGreyMui._50};
    font-weight: 700;
    color: ${blueGreyMui._700};
  }

  .hub-schedule__name {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-inline-end: 8px;
  }

  .hub-schedule__name--clickable {
    cursor: pointer;
    color: ${blueGreyMui._900};
    text-decoration: underline;
    transition: color 0.2s ease;

    &:hover {
      color: ${greyMui.A700};
    }
  }

  .hub-schedule__passengers {
    text-align: center;
    font-weight: 700;
  }

  .hub-schedule__row--expandable {
    cursor: pointer;
  }

  .hub-schedule__row--booking {
    padding-left: 32px;
    background-color: ${greyMui._100};
    grid-template-columns: minmax(140px, 180px) minmax(120px, 150px) minmax(
        0,
        1fr
      );
    align-items: start;
    gap: 12px;
    font-size: 11px;
    color: ${blueGreyMui._700};
  }

  .hub-schedule__booking-main {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .hub-schedule__expand-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 4px;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    width: 16px;
    height: 16px;
  }

  .hub-schedule__expand-icon {
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid ${blueGreyMui._600};
    transition: transform 0.2s ease;
    transform: rotate(0deg);

    &--expanded {
      transform: rotate(180deg);
    }
  }

  .hub-schedule__booking-id {
    font-weight: 600;
    color: ${blueGreyMui._800};

    &--clickable {
      cursor: pointer;
      text-decoration: underline;
      color: ${blueGreyMui._900};
      transition: color 0.2s ease;

      &:hover {
        color: ${greyMui.A700};
      }
    }
  }

  .hub-schedule__booking-time {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-weight: 500;
    color: ${blueGreyMui._700};
    white-space: nowrap;
  }

  .hub-schedule__booking-time-line {
    display: block;
  }

  .hub-schedule__booking-time-label {
    color: ${blueGreyMui._600};
    font-weight: 400;
  }

  .hub-schedule__booking-stations {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
  }

  .hub-schedule__station {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &--clickable {
      cursor: pointer;
      text-decoration: underline;
      color: ${blueGreyMui._900};
      transition: color 0.2s ease;

      &:hover {
        color: ${greyMui.A700};
      }
    }
  }

  .hub-schedule__booking-passenger {
    min-width: 0;
  }

  .hub-schedule__passenger-text {
    display: block;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 500;
    color: ${blueGreyMui._800};
  }

  .hub-schedule__passenger-code {
    font-weight: 600;
    color: ${blueGreyMui._800};
    margin-inline-end: 4px;

    &--clickable {
      cursor: pointer;
      text-decoration: underline;
      color: ${blueGreyMui._900};
      transition: color 0.2s ease;

      &:hover {
        color: ${greyMui.A700};
      }
    }
  }

  .hub-schedule__row--booking .course-schedule__station,
  .hub-schedule__row--booking .course-schedule__booking-passenger,
  .hub-schedule__row--booking .course-schedule__passenger-text,
  .hub-schedule__row--booking .course-schedule__passenger-code {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .hub-schedule__row--booking .course-schedule__station--clickable,
  .hub-schedule__row--booking .course-schedule__passenger-code--clickable {
    cursor: pointer;
    text-decoration: underline;
    color: ${blueGreyMui._900};
  }
`;
